<template>
	<section class="invoice-summary" aria-label="Supplier invoice summary">
		<template v-if="items.length">
			<h3 class="section-heading">Purchases</h3>
			<div class="purchases-scroll">
				<table aria-label="Purchases from supplier invoice" :class="{ 'has-purchase-actions': invoiceId }">
					<colgroup><col class="reference-column"><col class="description-column"><col class="amount-column"><col class="amount-column"><col class="amount-column"><col v-if="invoiceId" class="action-column"></colgroup>
					<thead><tr><th scope="col">Reference</th><th scope="col">Description</th><th scope="col">Unit price</th><th scope="col">Qty</th><th scope="col">Total</th><th v-if="invoiceId" scope="col"><span class="visually-hidden">Purchases journal</span></th></tr></thead>
					<tbody>
						<tr v-for="{ item, index } in purchaseRows" :key="index" :class="{ 'purchase-added': highlighted.has(index) }">
							<td>{{ item.reference || '—' }}</td>
							<td>{{ item.description || 'Unclear expense' }}<small v-if="item.needsReview || amountInPence(item.amount) === null">Check this item against the invoice.</small></td>
							<td>{{ money(amountInPence(item.unitPrice)) }}</td>
							<td>{{ item.quantity ?? '—' }}</td>
							<td>{{ money(amountInPence(item.amount)) }}</td>
							<td v-if="invoiceId" class="purchase-action">
								<span v-if="imports[index] && !imports[index].removed" class="added-label" role="status">✓ Added</span>
								<button v-else type="button" class="add-purchase-button" :disabled="checkingImports" :aria-label="`Add to Purchases: ${item.description}`" @click="selectedIndex = index">{{ checkingImports ? 'Checking…' : 'Add to Purchases' }}</button>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</template>
		<p v-if="notice" class="purchase-notice" role="status">{{ notice }}</p>
		<p v-if="importError" class="purchase-notice" role="alert">{{ importError }} <button type="button" class="add-purchase-button" @click="loadImports">Retry</button></p>
		<InvoicePurchaseImportDialog v-if="selectedIndex !== null" :key="`${invoiceId}:${selectedIndex}`" :invoice-id="invoiceId" :index="selectedIndex" :item="extraction.items[selectedIndex]" @close="selectedIndex = null" @added="handleAdded" />
		<footer class="invoice-reconciliation">
			<div v-if="$slots['invoice-action']" class="invoice-action">
				<slot name="invoice-action" />
			</div>
			<div class="reconciliation-details">
				<h3 class="section-heading">Summary</h3>
				<dl class="summary-breakdown">
					<div><dt>Services <span v-if="appointmentsHours" class="subtotal-hours">· {{ appointmentsHours }}</span></dt><dd>{{ money(appointmentsPence) }}</dd></div>
					<div v-if="items.length || purchasesPence === null"><dt>Purchases</dt><dd>{{ money(purchasesPence) }}</dd></div>
					<div class="reconciliation-total"><dt>Total</dt><dd>{{ money(summaryPence) }}</dd></div>
				</dl>
				<dl class="invoice-comparison">
					<div><dt>Invoice #{{ invoice.invoiceNumber || '—' }}</dt><dd>{{ money(invoicePence) }}</dd></div>
					<div :class="{ 'reconciliation-difference': difference !== null && difference !== 0 }"><dt>Difference</dt><dd>{{ money(difference) }}<span v-if="difference === 0" class="match-label">Matches invoice</span></dd></div>
				</dl>
			</div>
		</footer>
	</section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import InvoicePurchaseImportDialog from './InvoicePurchaseImportDialog.vue';
import { listInvoicePurchases } from '@/services/invoicePurchases';
const props = defineProps({
	invoice: { type: Object, required: true },
	appointmentsTotal: { type: Number, default: null },
	appointmentsHours: { type: String, default: '' },
});
const amountInPence = (value) => {
	if (!['number', 'string'].includes(typeof value) || String(value).trim() === '') return null;
	const amount = Number(value);
	return Number.isFinite(amount) ? Math.round((amount + Math.sign(amount) * Number.EPSILON) * 100) : null;
};
const money = (pence) => pence === null ? '—' : new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pence / 100);
const extraction = computed(() => props.invoice.purchaseExtraction);
const items = computed(() => Array.isArray(extraction.value?.items) ? extraction.value.items.filter((item) => item && typeof item === 'object') : []);
const purchaseRows = computed(() => Array.isArray(extraction.value?.items) ? extraction.value.items.map((item, index) => ({ item, index })).filter(({ item }) => item && typeof item === 'object') : []);
const invoiceId = computed(() => props.invoice.documentId || props.invoice.id || '');
const imports = ref({}), highlighted = ref(new Set()), selectedIndex = ref(null), checkingImports = ref(false), importError = ref('');
const highlightTimers = new Map();
let loadVersion = 0;
const clearHighlights = () => { highlightTimers.forEach(clearTimeout); highlightTimers.clear(); highlighted.value = new Set(); };
const loadImports = async () => {
  const version = ++loadVersion;
  checkingImports.value = Boolean(invoiceId.value && items.value.length);
  importError.value = '';
  if (!checkingImports.value) return;
  try {
    const records = await listInvoicePurchases(invoiceId.value);
    if (version === loadVersion) imports.value = Object.fromEntries(records.map((record) => [record.lineIndex, record]));
  } catch (error) { if (version === loadVersion) importError.value = error.message; }
  finally { if (version === loadVersion) checkingImports.value = false; }
};
const handleAdded = (result) => {
  const index = selectedIndex.value;
  if (index === null) return;
  imports.value = { ...imports.value, [index]: result };
  selectedIndex.value = null;
  if (result.alreadyAdded) return;
  highlighted.value = new Set([...highlighted.value, index]);
  highlightTimers.set(index, setTimeout(() => {
    const next = new Set(highlighted.value); next.delete(index); highlighted.value = next;
    highlightTimers.delete(index);
  }, 3000));
};
watch([invoiceId, extraction], () => { selectedIndex.value = null; imports.value = {}; clearHighlights(); void loadImports(); }, { immediate: true });
onBeforeUnmount(() => { loadVersion++; clearHighlights(); });
const purchasesPence = computed(() => {
	if (!extraction.value) return 0;
	if (extraction.value?.version !== 1 || extraction.value?.status !== 'complete' || !Array.isArray(extraction.value?.items)) return null;
	if (items.value.length !== extraction.value.items.length || items.value.some((item) => item.needsReview !== false || amountInPence(item.amount) === null)) return null;
	return items.value.reduce((sum, item) => sum + amountInPence(item.amount), 0);
});
const notice = computed(() => {
	if (!extraction.value) return '';
	if (extraction.value.status === 'failed') return 'Purchases could not be read. Check the original invoice.';
	if (purchasesPence.value === null) return 'Some expenses need checking against the invoice. The summary total is not yet available.';
	return '';
});
const appointmentsPence = computed(() => amountInPence(props.appointmentsTotal));
const invoicePence = computed(() => amountInPence(props.invoice.totalAmount));
const summaryPence = computed(() => appointmentsPence.value === null || purchasesPence.value === null ? null : appointmentsPence.value + purchasesPence.value);
const difference = computed(() => summaryPence.value === null || invoicePence.value === null ? null : summaryPence.value - invoicePence.value);
</script>

<style scoped>
.invoice-summary { container-type: inline-size; padding-bottom: 12px; color: var(--text-color, #414552); font: var(--ap-type-body); }
.section-heading { display: flex; align-items: center; min-height: 40px; box-sizing: border-box; margin: 0; padding: 0 16px; font: var(--ap-type-section); }
.purchases-scroll { overflow-x: auto; }
table { width: 100%; min-width: 580px; table-layout: fixed; border-collapse: collapse; background: rgb(255 255 255 / 94%); border: 1px solid var(--border-color, #dcdcdc); }
/* Keep prices, quantities and totals aligned with Rate, Hours and Total above. */
.reference-column { width: 11.1111%; }
.description-column { width: 33.3333%; }
.amount-column { width: 18.5185%; }
.action-column { width: 160px; }
.has-purchase-actions { min-width: 800px; }
.has-purchase-actions .reference-column { width: calc((100% - 160px) / 9); }
.has-purchase-actions .description-column { width: calc((100% - 160px) / 3); }
.has-purchase-actions .amount-column { width: calc((100% - 160px) * 5 / 27); }
.purchase-action { text-align: right; }
.add-purchase-button { border: 0; padding: 0; background: transparent; color: var(--noc-blue); font: var(--ap-type-control); cursor: pointer; }
.add-purchase-button:disabled { opacity: .55; cursor: wait; }
.added-label { color: #35734d; font: var(--ap-type-control); }
.purchase-added td { background: #e3f3e8; }
.visually-hidden { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
th, td { padding: 10px 16px; text-align: left; border-bottom: 1px solid var(--border-color, #dcdcdc); }
th { font: var(--ap-type-table-label); font-weight: 600; background: rgb(250 250 250 / 35%); }
td { vertical-align: top; overflow-wrap: anywhere; }
td + td { border-left: 1px solid var(--border-color, #dcdcdc); }
td:nth-child(n+3) { font-variant-numeric: tabular-nums; }
small { display: block; color: #a4332a; font-size: 12px; }
.purchase-notice { margin: 12px 16px; color: var(--text-color-secondary, #5e6170); }
.invoice-reconciliation { display: grid; grid-template-columns: minmax(0, 1fr) max(37.037%, 360px); column-gap: 16px; margin-top: 24px; }
.invoice-action { display: flex; justify-content: flex-end; align-items: center; align-self: start; min-height: 40px; }
.reconciliation-details { grid-column: 2; min-width: 0; }
dl { margin: 0; }
dl > div { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
dt, dd { margin: 0; padding: 8px 16px; }
dd { font-variant-numeric: tabular-nums; }
.subtotal-hours { color: var(--text-color-secondary, #5e6170); white-space: nowrap; }
.reconciliation-total { margin-top: 4px; background: var(--noc-blue-05, rgba(10, 97, 201, 0.05)); border-radius: 6px; font: var(--ap-type-total); }
.invoice-comparison { margin-top: 12px; color: var(--text-color-secondary, #5e6170); }
.reconciliation-difference { color: #a4332a; }
.match-label { display: block; margin-top: 2px; color: #35734d; font: var(--ap-type-caption); }

@container (max-width: 560px) {
	.invoice-reconciliation { grid-template-columns: minmax(0, 1fr); row-gap: 8px; }
	.invoice-action { justify-content: flex-start; padding-inline: 16px; }
	.reconciliation-details { grid-column: 1; }
}

@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
	table {
		background: linear-gradient(135deg, rgb(255 255 255 / 72%), rgb(255 255 255 / 48%));
		-webkit-backdrop-filter: blur(16px) saturate(115%);
		backdrop-filter: blur(16px) saturate(115%);
	}
}

@media (prefers-reduced-transparency: reduce) {
	table {
		background: #fff;
		-webkit-backdrop-filter: none;
		backdrop-filter: none;
	}
}
</style>
